import {
  Component,
  OnDestroy,
  signal,
  inject,
  computed,
  effect,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';

import { RouteService } from '../../../../route/route.service';
import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';
import { IPagination } from 'app/common/interfaces';
import RouteAttributes from '@albatrosdeveloper/ave-models-package/src/schemas/routing/route/route.entity';

import { AssignTerritoryModalService } from '../assign-territory-modal.service';
import {
  TerritoryRowView,
  createTerritoryRowView,
} from '../interfaces/assign-territory-modal.interface';

interface ApiResponse {
  rows: TerritoryRowView[];
  total: number;
}

@Component({
  selector: 'app-territory-step',
  templateUrl: './territory-step.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatCheckboxModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatTooltipModule,
    FormSectionComponent,
  ],
})
export class TerritoryStepComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  // Inject services
  readonly modalService = inject(AssignTerritoryModalService);
  private readonly routeService = inject(RouteService);

  // Search - convert FormControl to signal
  searchCtrl = new FormControl('');
  private readonly searchTerm = toSignal(
    this.searchCtrl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      startWith('')
    ),
    { initialValue: '' }
  );

  // Pagination signals
  pageSize = signal(10);
  currentPage = signal(1);
  pageSizeOptions = [5, 10, 25];

  // Loading state
  isLoadingTerritories = signal(false);

  // API response data
  private apiResponse = signal<ApiResponse | null>(null);

  // Table
  territoriesDataSource = new MatTableDataSource<TerritoryRowView>([]);
  displayedColumns = ['select', 'code', 'name', 'company'];

  // Computed: table data that combines API data with current selection state
  readonly tableData = computed(() => {
    const response = this.apiResponse();
    if (!response) return [];

    const isSelected = this.modalService.isTerritorySelectedFn();

    // Update isSelected based on current selection state
    return response.rows.map(row => ({
      ...row,
      isSelected: isSelected(row._id)
    }));
  });

  // Computed: total territories from API response
  readonly totalTerritories = computed(() => this.apiResponse()?.total || 0);

  // Effect: load data when search, page, or companies change
  private loadEffect = effect(() => {
    const companies = this.modalService.clientCompanies();
    const search = this.searchTerm();
    const page = this.currentPage();
    const size = this.pageSize();

    // Filter companies with valid id
    const validCompanies = companies.filter((c): c is typeof c & { id: number } => c.id != null);

    if (validCompanies.length === 0) {
      untracked(() => this.apiResponse.set({ rows: [], total: 0 }));
      return;
    }

    // Use untracked for the API call to avoid tracking the response signal
    untracked(() => this.loadFromApi(validCompanies, search || '', page, size));
  });

  // Effect: sync MatTableDataSource when tableData changes
  private tableEffect = effect(() => {
    this.territoriesDataSource.data = this.tableData();
  });

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load territories from API
   */
  private loadFromApi(
    companies: { id: number }[],
    search: string,
    page: number,
    size: number
  ): void {
    this.isLoadingTerritories.set(true);
    const companyIds = companies.map(c => c.id);

    const options: IPagination<RouteAttributes> = {
      currentPage: page,
      limitPerPage: size,
      search: search || undefined,
      sort: 'name',
      sortDirection: 'ASC',
    };

    this.routeService
      .findAllWithCountByCompanyIds(options, companyIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const responseData = res.data[0];
          const assignedIds = this.modalService.assignedTerritoryIds();
          const isSelected = this.modalService.isTerritorySelectedFn();

          const rows = responseData.data.map(route =>
            createTerritoryRowView(
              route,
              assignedIds,
              isSelected(route._id.toString())
            )
          );

          this.apiResponse.set({
            rows,
            total: responseData.pageInfo?.[0]?.total || 0
          });
          this.isLoadingTerritories.set(false);
        },
        error: () => {
          this.isLoadingTerritories.set(false);
        },
      });
  }

  // Computed: check if all items on current page are selected
  readonly isAllSelected = computed(() => {
    const data = this.tableData();
    return data.length > 0 && data.every(row => row.isSelected);
  });

  // Computed: check if some but not all items are selected (for indeterminate state)
  readonly isIndeterminate = computed(() => {
    const data = this.tableData();
    const selectedCount = data.filter(r => r.isSelected).length;
    return selectedCount > 0 && selectedCount < data.length;
  });

  /**
   * Toggle row selection - simplified, no manual sync needed
   */
  toggleRow(row: TerritoryRowView): void {
    this.modalService.toggleTerritory(row);
    // tableData computed recalculates automatically
  }

  /**
   * Toggle all rows on current page - simplified
   */
  masterToggle(): void {
    if (this.isAllSelected()) {
      this.modalService.deselectAllTerritories(this.tableData());
    } else {
      this.modalService.selectAllTerritories(this.tableData());
    }
    // tableData computed recalculates automatically
  }

  /**
   * Handle page change
   */
  onPageChange(event: PageEvent): void {
    this.currentPage.set(event.pageIndex + 1);
    this.pageSize.set(event.pageSize);
    // loadEffect will trigger automatically
  }

  /**
   * Remove territory from selection (from chip)
   */
  removeFromSelection(territory: TerritoryRowView): void {
    this.modalService.removeTerritory(territory);
    // tableData computed recalculates automatically
  }

  /**
   * Clear search
   */
  clearSearch(): void {
    this.searchCtrl.setValue('');
  }
}
